import { useSignUp, useAuth } from '@clerk/clerk-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Link, useNavigate } from 'react-router-dom';
import { LanguageSelector } from './language-selector';
import {
  buildSignUpUnsafeMetadata,
  useAcquisitionTracking,
} from '@/hooks/use-acquisition-tracking';
import { saveOnboardingIntent, getOnboardingState } from '@/lib/onboarding';
import { brandingConfig } from '@/config/branding';
import { BrandLogo } from '@/components/branding/brand-logo';
import { useEffectiveBranding } from '@/hooks/use-effective-branding';
import { useBrandTheme } from '@/hooks/use-brand-theme';
import { trackXSignup } from '@/lib/analytics/x-pixel';
import { setGaIdentityFromSignup, trackGaSignUp } from '@/lib/analytics/ga';

/**
 * First-touch acquisition + device thumbmarks attached at signUp.create AND
 * at authenticateWithRedirect so the Clerk user is born with them (read by
 * the CRM admin at org creation). Spread into every create call, including
 * OTP retry paths that recreate the signUp attempt.
 */
const signUpUnsafeMetadata = buildSignUpUnsafeMetadata;

export function CustomSignUp() {
  const { t } = useTranslation('auth');
  const { signUp, setActive, isLoaded } = useSignUp();
  const { isSignedIn } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const brand = useEffectiveBranding();
  useBrandTheme(brand);

  // Track acquisition data (UTM params, referrer, etc.)
  useAcquisitionTracking();

  // Set up onboarding intent for new signups (if not already set from marketplace)
  useEffect(() => {
    const existingState = getOnboardingState();

    // Only create a generic onboarding intent if one doesn't exist
    // (marketplace signups will already have a plugin_install intent)
    if (!existingState) {
      saveOnboardingIntent({
        type: 'store_create',
        source: 'direct_signup',
      });
      console.log('✅ Generic onboarding intent created for new signup');
    } else {
      console.log('ℹ️ Existing onboarding intent found:', existingState.intent?.type);
    }
  }, []);

  // Redirect to home if already signed in
  useEffect(() => {
    if (isSignedIn) {
      navigate('/');
    }
  }, [isSignedIn, navigate]);

  const finishSignup = async (sessionId: string, emailAddress: string) => {
    if (!setActive) throw new Error('Clerk setActive unavailable');
    await setActive({ session: sessionId });
    await trackXSignup({ email: emailAddress, conversionId: sessionId });
    setGaIdentityFromSignup({ firstName, lastName, email: emailAddress });
    trackGaSignUp({ method: 'email' });
    navigate('/');
  };

  // Email + OTP sign-up (no password)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded) return;

    setLoading(true);
    setError('');

    try {
      await signUp.create({
        emailAddress: email,
        firstName,
        lastName,
        ...(await signUpUnsafeMetadata()),
      });

      // Send verification email with OTP
      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      setVerifying(true);
    } catch (err: any) {
      console.error('Sign up error:', err);
      setError(err.errors?.[0]?.message || 'Sign up failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Verify email code
  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded) return;

    setLoading(true);
    setError('');

    try {
      // Check if email is already verified
      const currentVerificationStatus = signUp.verifications.emailAddress?.status;

      if (currentVerificationStatus === 'verified') {
        // Email already verified, try to complete signup
        const result = await signUp.create({
          emailAddress: email,
          firstName,
          lastName,
          ...(await signUpUnsafeMetadata()),
        });

        if (result.status === 'complete' && result.createdSessionId) {
          await finishSignup(result.createdSessionId, email);
          return;
        }
      }

      // Otherwise, attempt verification
      const result = await signUp.attemptEmailAddressVerification({ code });

      if (result.status === 'complete' && result.createdSessionId) {
        await finishSignup(result.createdSessionId, email);
      } else if (result.status === 'missing_requirements') {
        // Check what's missing
        console.error('Signup incomplete:', {
          status: result.status,
          missingFields: result.missingFields,
          unverifiedFields: result.unverifiedFields,
        });
        setError('Signup configuration error. Please contact support.');
      }
    } catch (err: any) {
      console.error('Verification error:', err);
      const errorCode = err.errors?.[0]?.code;
      const errorMessage = err.errors?.[0]?.message;

      // Handle specific error cases
      if (errorCode === 'verification_already_verified') {
        setError('Email already verified. Redirecting...');
        // Try to complete signup without re-verification
        try {
          const result = await signUp.create({
            emailAddress: email,
            firstName,
            lastName,
            ...(await signUpUnsafeMetadata()),
          });
          if (result.createdSessionId) {
            await finishSignup(result.createdSessionId, email);
            return;
          }
        } catch (retryErr) {
          setError('Unable to complete signup. Please try signing in instead.');
        }
      } else {
        setError(errorMessage || 'Verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Google OAuth sign-up
  const handleGoogleSignUp = async () => {
    if (!isLoaded) return;

    setLoading(true);
    setError('');

    try {
      await signUp.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: '/sso-callback',
        // Forced to ?signin=1 in SSOCallbackProcessor; Sign-up fires there.
        redirectUrlComplete: '/sso-callback?signin=1',
        // Acquisition + thumbmarks ride the OAuth bounce (TAG-2514).
        ...(await signUpUnsafeMetadata()),
      });
    } catch (err: any) {
      console.error('Google sign up error:', err);
      setError(err.errors?.[0]?.message || 'Google sign up failed. Please try again.');
      setLoading(false);
    }
  };

  // Verification screen
  if (verifying) {
    return (
      <div className="flex min-h-screen items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="flex justify-center">
            <BrandLogo brand={brand} />
          </div>
          <div className="space-y-2 text-center">
            <h1 className="text-foreground text-3xl font-bold tracking-tight">
              {t('signUp.verification.title')}
            </h1>
            <p className="text-muted-foreground">
              {t('signUp.verification.subtitle')} <strong>{email}</strong>
            </p>
          </div>

          <form onSubmit={handleVerify} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code">{t('signUp.verification.codeLabel')}</Label>
              <Input
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={t('signUp.verification.codePlaceholder')}
                className="text-center text-2xl tracking-widest"
                maxLength={6}
                required
                disabled={loading}
              />
              <p className="text-muted-foreground text-xs">{t('signUp.verification.codeHelper')}</p>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-3 dark:bg-red-900/20">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="border-primary-foreground h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                  {t('signUp.verification.verifying')}
                </div>
              ) : (
                t('signUp.verification.verifyButton')
              )}
            </Button>

            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => setVerifying(false)}
              disabled={loading}
            >
              {t('signUp.verification.backButton')}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Sign-up screen
  return (
    <div className="flex min-h-screen">
      {/* Left side - Form */}
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Language Selector */}
          <div className="mb-4 flex justify-end">
            <LanguageSelector />
          </div>

          <div className="space-y-6">
            <div className="flex justify-center">
              <BrandLogo brand={brand} />
            </div>
            <div className="space-y-2 text-center">
              <h1 className="text-foreground text-3xl font-bold tracking-tight">{t('signUp.title')}</h1>
              <p className="text-muted-foreground">{t('signUp.subtitle')}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">{t('signUp.firstName')}</Label>
                  <Input
                    id="firstName"
                    placeholder={t('signUp.firstNamePlaceholder')}
                    type="text"
                    autoCapitalize="none"
                    autoComplete="given-name"
                    autoCorrect="off"
                    disabled={loading}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">{t('signUp.lastName')}</Label>
                  <Input
                    id="lastName"
                    placeholder={t('signUp.lastNamePlaceholder')}
                    type="text"
                    autoCapitalize="none"
                    autoComplete="family-name"
                    autoCorrect="off"
                    disabled={loading}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t('signUp.email')}</Label>
                <Input
                  id="email"
                  placeholder={t('signUp.emailPlaceholder')}
                  type="email"
                  autoCapitalize="none"
                  autoComplete="email"
                  autoCorrect="off"
                  disabled={loading}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <p className="text-muted-foreground text-xs">
                  {t('signUp.otpHelper')}
                </p>
              </div>

              <div className="flex items-start space-x-2">
                <Checkbox id="terms" required />
                <label
                  htmlFor="terms"
                  className="text-muted-foreground cursor-pointer text-sm leading-relaxed"
                >
                  {t('signUp.termsPrefix')}{' '}
                  {brandingConfig.appUrl ? (
                    <a
                      href={`${brandingConfig.appUrl}/terms`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-foreground hover:text-primary underline underline-offset-4"
                    >
                      {t('signUp.termsLink')}
                    </a>
                  ) : (
                    <span className="text-foreground">{t('signUp.termsLink')}</span>
                  )}{' '}
                  {t('signUp.termsAnd')}{' '}
                  {brandingConfig.appUrl ? (
                    <a
                      href={`${brandingConfig.appUrl}/privacy`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-foreground hover:text-primary underline underline-offset-4"
                    >
                      {t('signUp.privacyLink')}
                    </a>
                  ) : (
                    <span className="text-foreground">{t('signUp.privacyLink')}</span>
                  )}
                </label>
              </div>

              {error && (
                <div className="rounded-md bg-red-50 p-3 dark:bg-red-900/20">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              <Button className="w-full" type="submit" disabled={loading}>
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="border-primary-foreground h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                    {t('signUp.creating')}
                  </div>
                ) : (
                  t('signUp.createButton')
                )}
              </Button>
            </form>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="border-border w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background text-muted-foreground px-2">{t('signUp.orContinueWith')}</span>
              </div>
            </div>

            <Button
              variant="outline"
              type="button"
              className="w-full"
              disabled={loading}
              onClick={handleGoogleSignUp}
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              {t('signUp.continueWithGoogle')}
            </Button>

            <p className="text-muted-foreground text-center text-sm">
              {t('signUp.haveAccount')}{' '}
              <Link
                to="/sign-in"
                className="text-foreground hover:text-primary font-semibold underline underline-offset-4"
              >
                {t('signUp.signInLink')}
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Feature showcase */}
      <div className="border-border bg-card hidden flex-1 items-center justify-center border-l p-12 lg:flex">
        <div className="max-w-lg space-y-8">
          <div className="space-y-4">
            <h2 className="text-card-foreground text-balance text-4xl font-bold">
              {t('signUp.features.title')}
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed">{t('signUp.features.subtitle')}</p>
          </div>

          <div className="space-y-6">
            <div className="flex gap-4">
              <div className="bg-primary/10 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg">
                <svg className="text-primary h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <div className="space-y-1">
                <h3 className="text-card-foreground font-semibold">{t('signUp.features.feature1.title')}</h3>
                <p className="text-muted-foreground text-sm">{t('signUp.features.feature1.description')}</p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="bg-primary/10 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg">
                <svg className="text-primary h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                  />
                </svg>
              </div>
              <div className="space-y-1">
                <h3 className="text-card-foreground font-semibold">{t('signUp.features.feature2.title')}</h3>
                <p className="text-muted-foreground text-sm">{t('signUp.features.feature2.description')}</p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="bg-primary/10 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg">
                <svg className="text-primary h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <div className="space-y-1">
                <h3 className="text-card-foreground font-semibold">{t('signUp.features.feature3.title')}</h3>
                <p className="text-muted-foreground text-sm">{t('signUp.features.feature3.description')}</p>
              </div>
            </div>
          </div>

          <div className="border-border border-t pt-6">
            <p className="text-muted-foreground text-sm">
              {t('signUp.features.trustedBy')}{' '}
              <span className="text-card-foreground font-semibold">
                {t('signUp.features.merchantsCount')}
              </span>{' '}
              {t('signUp.features.merchantsText')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
